import React from 'react';
import { Box, ButtonBase, Chip, Collapse, Paper, Stack, Typography } from '@mui/material';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import BedtimeOutlinedIcon from '@mui/icons-material/BedtimeOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import { getEntryDate, getEntryId } from '../../components/Timeline/utils/medicationTimelinePatternObservationUtils';

const getEntryLabel = (entry = {}) => (
  entry.displayTitle || entry.title || entry.content || entry.notes || entry.description || 'Timeline entry'
);

const formatEntryDate = (entry) => getEntryDate(entry)?.toLocaleDateString('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
}) || '';

const PATTERN_ICON_CONFIG = {
  frequency_change: { Icon: TrendingUpRoundedIcon, label: 'Change insight' },
  repeated_cooccurrence: { Icon: BedtimeOutlinedIcon, label: 'Sleep connection' },
  what_seems_to_help: { Icon: LightbulbOutlinedIcon, label: 'Helpful strategy' },
  new_notable_change: { Icon: NewReleasesOutlinedIcon, label: 'Notable observation' },
  medication_timing: { Icon: MedicationOutlinedIcon, label: 'Medication timing' },
};

const PatternIcon = ({ type }) => {
  const { Icon, label } = PATTERN_ICON_CONFIG[type] || { Icon: LinkRoundedIcon, label: 'Care insight' };
  return (
    <Box
      role="img"
      aria-label={label}
      sx={{ width: 38, height: 38, borderRadius: '13px', display: 'grid', placeItems: 'center', flexShrink: 0, bgcolor: colors.semantic.sageMist, color: colors.semantic.sageActionInk }}
    >
      <Icon sx={{ fontSize: 21 }} />
    </Box>
  );
};

const PatternCard = ({ pattern, entriesById, highlighted = false, onViewEntry }) => {
  const [expanded, setExpanded] = React.useState(false);
  const evidenceEntries = pattern.evidenceEntryIds.map((id) => entriesById.get(id)).filter(Boolean);

  return (
    <Paper elevation={0} sx={{ p: { xs: 1.7, sm: 2 }, borderRadius: '20px', border: `1px solid ${highlighted ? colors.semantic.sageAction : colors.semantic.line}`, bgcolor: highlighted ? colors.semantic.sageMist : '#FFFFFF', boxShadow: highlighted ? `0 8px 24px ${alpha(colors.semantic.sageActionInk, 0.08)}` : 'none' }}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
            <PatternIcon type={pattern.type} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 850, color: colors.semantic.ink, lineHeight: 1.2 }}>{pattern.title}</Typography>
              <Typography sx={{ mt: 0.35, fontSize: '0.76rem', color: colors.semantic.ink2 }}>{pattern.dateRange.label}</Typography>
            </Box>
          </Stack>
          <Chip label={pattern.confidence} size="small" sx={{ bgcolor: colors.semantic.sageMist, color: colors.semantic.sageActionInk, fontWeight: 750 }} />
        </Stack>

        <Typography sx={{ color: colors.semantic.ink, fontSize: '0.94rem', lineHeight: 1.55 }}>{pattern.finding}</Typography>
        {pattern.disclaimer ? <Typography sx={{ color: colors.semantic.ink2, fontSize: '0.8rem', lineHeight: 1.45 }}>{pattern.disclaimer}</Typography> : null}

        <ButtonBase onClick={() => setExpanded((current) => !current)} sx={{ alignSelf: 'flex-start', borderRadius: '999px', color: colors.semantic.sageActionInk }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 850 }}>
            {expanded ? 'Hide entries' : `View ${pattern.supportingRecordCount} supporting ${pattern.supportingRecordCount === 1 ? 'entry' : 'entries'}`}
          </Typography>
        </ButtonBase>

        <Collapse in={expanded} unmountOnExit>
          <Stack spacing={0.55} sx={{ pt: 0.35 }}>
            {evidenceEntries.map((entry) => (
              <ButtonBase
                key={getEntryId(entry)}
                onClick={() => onViewEntry?.(entry)}
                sx={{ width: '100%', justifyContent: 'space-between', gap: 1, p: 1, borderRadius: '14px', border: `1px solid ${colors.semantic.line}`, bgcolor: colors.semantic.card, textAlign: 'left' }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: '0.84rem', fontWeight: 750, color: colors.semantic.ink }}>{getEntryLabel(entry)}</Typography>
                  <Typography sx={{ mt: 0.15, fontSize: '0.72rem', color: colors.semantic.ink2 }}>{formatEntryDate(entry)}</Typography>
                </Box>
                <KeyboardArrowRightRoundedIcon sx={{ color: alpha(colors.semantic.ink, 0.5), flexShrink: 0 }} />
              </ButtonBase>
            ))}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
};

const CarePatternsSection = ({ patterns = [], entries = [], highlightedPatternId = '', onViewEntry }) => {
  const entriesById = React.useMemo(() => new Map(entries.map((entry) => [getEntryId(entry), entry])), [entries]);
  const orderedPatterns = React.useMemo(() => [...patterns].sort((left, right) => (
    Number(right.id === highlightedPatternId) - Number(left.id === highlightedPatternId)
  )), [highlightedPatternId, patterns]);

  return (
    <Box component="section" aria-labelledby="care-patterns-title">
      <Typography id="care-patterns-title" component="h2" sx={{ fontSize: { xs: '1.25rem', sm: '1.45rem' }, fontWeight: 900, color: colors.semantic.ink }}>
        What&apos;s changing
      </Typography>
      <Typography sx={{ mt: 0.35, mb: 1.25, color: colors.semantic.ink2, fontSize: '0.9rem' }}>
        Possible patterns from saved care records. Sometimes there may be nothing useful to show.
      </Typography>

      {orderedPatterns.length ? (
        <Stack spacing={1.1}>
          {orderedPatterns.map((pattern) => <PatternCard key={pattern.id} pattern={pattern} entriesById={entriesById} highlighted={pattern.id === highlightedPatternId} onViewEntry={onViewEntry} />)}
        </Stack>
      ) : (
        <Paper elevation={0} sx={{ p: 2, borderRadius: '18px', border: `1px solid ${colors.semantic.line}`, bgcolor: colors.semantic.card }}>
          <Typography sx={{ fontWeight: 800, color: colors.semantic.ink }}>No clear pattern yet</Typography>
          <Typography sx={{ mt: 0.35, color: colors.semantic.ink2, fontSize: '0.86rem', lineHeight: 1.45 }}>
            Lifelog will only show a pattern when enough supporting records are available.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default CarePatternsSection;
